import Phaser from 'phaser';

/**
 * TouchControls — controles táctiles para GUAYABERA-Z.
 * GDD §18. Versión DOM overlay para coordenadas físicas correctas.
 *
 * Problema anterior (S20 Ultra landscape): con Scale FIT el canvas se letterboxea
 * (ej. 732×412 en viewport 915×412, offset 91px). Los objetos Phaser con
 * setScrollFactor(0) usan coords lógicas 1280×720 escaladas, el hitArea se
 * reduce a ~20px CSS y el offset de letterbox provoca miss.
 * Solución: overlay HTML absoluto sobre #game-container, coords viewport reales,
 * hit 44px+ CSS, multitouch nativo, sin transform de Phaser.
 */
export class TouchControls {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container | null = null;
  private buttons: Phaser.GameObjects.Container[] = [];
  private fullscreenBtn: Phaser.GameObjects.Container | null = null;
  private fullscreenIcon: Phaser.GameObjects.Text | null = null;
  private resizeHandler: (() => void) | null = null;

  // DOM overlay
  private domRoot: HTMLDivElement | null = null;
  private domButtons: Map<string, HTMLDivElement> = new Map();
  private domOriginalParent: HTMLElement | null = null;
  private boundReparent = () => this.reparentDomForFullscreen(!!document.fullscreenElement);

  // Held states
  private leftHeld = false;
  private rightHeld = false;
  private sprintHeld = false;
  private jumpHeld = false;

  // Edge triggers
  private jumpPressPending = false;
  private jumpReleasePending = false;
  private stonePressPending = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  shouldShow(): boolean {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('mobile') || url.searchParams.has('touch')) return true;
      if (url.searchParams.has('desktop')) return false;
      if (localStorage.getItem('gz_forceTouch') === '1') return true;
      if (localStorage.getItem('gz_forceTouch') === '0') return false;
    } catch { /* ignore */ }
    const touchDevice = (this.scene.sys.game.device.input as unknown as { touch: boolean }).touch;
    if (touchDevice) return true;
    const hasTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    if (hasTouch) return true;
    const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
    const anyCoarse = window.matchMedia?.('(any-pointer: coarse)').matches ?? false;
    if (coarse || anyCoarse) return true;
    if (window.innerWidth <= 1024) {
      const uaMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
      if (uaMobile) return true;
      if (window.innerWidth < 900 && window.innerHeight < 900) return true;
    }
    return false;
  }

  create(): void {
    this.scene.input.addPointer(3);

    // Intentar DOM overlay primero (coords físicas, sin letterbox)
    const useDom = this.createDomOverlay();
    if (useDom) {
      // Mover overlay al fullscreenTarget cuando entra en fullscreen (Phaser mueve el canvas)
      this.scene.scale.on('enterfullscreen', () => { this.reparentDomForFullscreen(true); this.updateFullscreenIcon(); });
      this.scene.scale.on('leavefullscreen', () => { this.reparentDomForFullscreen(false); this.updateFullscreenIcon(); });
      this.scene.scale.on('fullscreenfailed', () => this.updateFullscreenIcon());
      // También escuchar evento nativo por si usa document.requestFullscreen fallback
      document.addEventListener('fullscreenchange', this.boundReparent as EventListener);
      this.scene.events.once('shutdown', () => {
        document.removeEventListener('fullscreenchange', this.boundReparent as EventListener);
        this.destroy();
      });
      return;
    }

    // Fallback Phaser (si DOM no disponible, ej. tests)
    const cw = this.scene.cameras.main.width;
    const ch = this.scene.cameras.main.height;
    this.container = this.scene.add.container(0, 0).setScrollFactor(0).setDepth(1000);
    const btnY = ch - 72;
    const pad = 16;
    this.addButton(78, btnY, 36, '◀', () => { this.leftHeld = true; }, () => { this.leftHeld = false; });
    this.addButton(78 + 72 + pad, btnY, 36, '▶', () => { this.rightHeld = true; }, () => { this.rightHeld = false; });
    // Phaser fallback sprint como toggle (para single-pointer DevTools)
    this.addButton(78 + 72 + pad + 54 + pad, btnY, 28, '≫', () => { this.sprintHeld = !this.sprintHeld; }, () => {}, 'RUN');
    const jumpX = cw - 82;
    const stoneX = cw - 82 - 72 - pad;
    this.addButton(stoneX, btnY, 32, '●', () => { this.stonePressPending = true; }, () => {}, 'TIRO');
    this.addButton(jumpX, btnY, 38, '▲', () => { this.jumpHeld = true; this.jumpPressPending = true; }, () => { this.jumpHeld = false; this.jumpReleasePending = true; }, 'SALTO');
    this.container.setVisible(this.shouldShow());
    if ((this.scene.scale as unknown as { fullscreen: { available: boolean } }).fullscreen.available) {
      this.addFullscreenButton();
    }
    this.resizeHandler = () => this.handleResize();
    this.scene.scale.on('resize', this.resizeHandler);
    this.scene.events.once('shutdown', () => this.destroy());
    this.scene.scale.on('enterfullscreen', () => this.updateFullscreenIcon());
    this.scene.scale.on('leavefullscreen', () => this.updateFullscreenIcon());
    this.scene.scale.on('fullscreenfailed', () => this.updateFullscreenIcon());
    this.scene.input.on('pointerdown', () => { /* unlock */ });
  }

  private createDomOverlay(): boolean {
    const parent = document.getElementById('game-container');
    if (!parent) return false;
    // Evitar duplicado
    if (parent.querySelector('#gz-touch-root')) return true;

    const root = document.createElement('div');
    root.id = 'gz-touch-root';
    root.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:10;';
    // Mostrar solo si shouldShow, sino ocultar pero mantener para toggle T
    root.style.display = this.shouldShow() ? 'block' : 'none';

    const updateSprintVisual = () => {
      const b = this.domButtons.get('sprint');
      if (!b) return;
      if (this.sprintHeld) {
        b.style.background = 'rgba(255,220,0,0.55)';
        b.style.borderColor = 'rgba(255,220,0,0.9)';
        b.style.transform = 'scale(0.94)';
      } else {
        b.style.background = 'rgba(0,0,0,0.35)';
        b.style.borderColor = 'rgba(255,255,255,0.55)';
        b.style.transform = 'scale(1)';
      }
    };
    const mkBtn = (id: string, left: string | null, right: string | null, bottom: string, size: number, icon: string, label?: string) => {
      const b = document.createElement('div');
      b.dataset.id = id;
      b.style.cssText = `position:absolute;${left!==null?`left:${left};`:''}${right!==null?`right:${right};`:''}bottom:${bottom};width:${size}px;height:${size}px;border-radius:50%;background:rgba(0,0,0,0.35);border:2px solid rgba(255,255,255,0.55);display:flex;align-items:center;justify-content:center;flex-direction:column;pointer-events:auto;touch-action:none;user-select:none;-webkit-user-select:none;`;
      const ic = document.createElement('span');
      ic.textContent = icon;
      ic.style.cssText = `font-size:${Math.round(size*0.45)}px;color:#fff;font-family:monospace;font-weight:bold;line-height:1;${label?'margin-top:2px;':''}`;
      b.appendChild(ic);
      if (label) {
        const lb = document.createElement('span');
        lb.textContent = label;
        lb.style.cssText = 'font-size:7px;color:#fff;font-family:monospace;margin-top:1px;opacity:0.9;';
        b.appendChild(lb);
      }
      // Feedback visual + pointer capture para hold y multitouch
      const setPressed = (p: boolean) => {
        if (id === 'sprint') { updateSprintVisual(); return; }
        b.style.background = p ? 'rgba(0,0,0,0.65)' : 'rgba(0,0,0,0.35)'; b.style.transform = p ? 'scale(0.94)' : 'scale(1)';
      };
      const down = (e: PointerEvent) => {
        e.preventDefault();
        try { (b as HTMLElement).setPointerCapture(e.pointerId); } catch { /* ignore */ }
        if (id !== 'sprint') setPressed(true);
        handleDown(id);
        if (id === 'sprint') updateSprintVisual();
      };
      const up = (e: PointerEvent) => {
        e.preventDefault();
        try { (b as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        if (id !== 'sprint') setPressed(false);
        handleUp(id);
        if (id === 'sprint') updateSprintVisual();
      };
      b.addEventListener('pointerdown', down as unknown as EventListener, { passive: false });
      b.addEventListener('pointerup', up as unknown as EventListener, { passive: false });
      b.addEventListener('pointercancel', up as unknown as EventListener, { passive: false });
      // No pointerleave release: con capture el dedo puede deslizarse sin soltar
      b.addEventListener('contextmenu', (e) => e.preventDefault());
      root.appendChild(b);
      this.domButtons.set(id, b);
      return b;
    };
    const handleDown = (id: string) => {
      if (id === 'left') this.leftHeld = true;
      if (id === 'right') this.rightHeld = true;
      if (id === 'sprint') {
        // Toggle + hold compatible: con mouse single-pointer no puedes mantener sprint+movimiento,
        // así que toggle permite probar. En móvil real con 2 dedos funciona como hold toggleado.
        this.sprintHeld = !this.sprintHeld;
        updateSprintVisual();
        console.log(`[TouchControls] RUN ${this.sprintHeld ? 'ON' : 'OFF'}`);
      }
      if (id === 'stone') this.stonePressPending = true;
      if (id === 'jump') { this.jumpHeld = true; this.jumpPressPending = true; }
      if (id === 'fs') {
        const b = this.domButtons.get('fs');
        if (b) b.style.background = 'rgba(0,0,0,0.65)';
        try {
          if (this.scene.scale.isFullscreen) this.scene.scale.stopFullscreen();
          else this.scene.scale.startFullscreen();
        } catch {
          try { if (document.fullscreenElement) document.exitFullscreen(); else document.documentElement.requestFullscreen?.(); } catch { /* ignore */ }
        }
      }
    };
    const handleUp = (id: string) => {
      if (id === 'left') this.leftHeld = false;
      if (id === 'right') this.rightHeld = false;
      // sprint es toggle, no se suelta en pointerup (así funciona con single-pointer en DevTools)
      if (id === 'jump') { this.jumpHeld = false; this.jumpReleasePending = true; }
      if (id === 'fs') {
        const b = this.domButtons.get('fs');
        if (b) b.style.background = 'rgba(0,0,0,0.35)';
        this.updateFullscreenIcon();
      }
    };

    // Izquierda abajo: ◀ ▶ ≫
    mkBtn('left', '12px', null, '16px', 64, '◀');
    mkBtn('right', '84px', null, '16px', 64, '▶');
    mkBtn('sprint', '156px', null, '16px', 56, '≫', 'RUN');
    // Derecha abajo: TIRO y SALTO
    mkBtn('stone', null, '84px', '16px', 60, '●', 'TIRO');
    mkBtn('jump', null, '12px', '16px', 68, '▲', 'SALTO');
    // Fullscreen arriba-der
    if ((this.scene.scale as unknown as { fullscreen: { available: boolean } }).fullscreen.available) {
      const fs = mkBtn('fs', null, '12px', 'auto', 36, '⛶');
      fs.style.top = '12px';
      fs.style.bottom = 'auto';
      fs.style.width = '36px'; fs.style.height = '36px';
    }

    parent.style.position = 'relative';
    parent.appendChild(root);
    this.domRoot = root;
    this.domOriginalParent = parent;
    this.updateFullscreenIcon();
    // Si ya está en fullscreen al crear, reparentea
    if (document.fullscreenElement) this.reparentDomForFullscreen(true);
    return true;
  }

  private handleResize(): void {
    if (!this.container) return;
    const cw = this.scene.cameras.main.width;
    const ch = this.scene.cameras.main.height;
    const btnY = ch - 72;
    const xs = [78, 78 + 72 + 16, 78 + 72 + 16 + 54 + 16, cw - 82 - 72 - 16, cw - 82];
    this.buttons.forEach((b, i) => { b.setPosition(xs[i], btnY); });
    if (this.fullscreenBtn) this.fullscreenBtn.setPosition(cw - 28, 28);
    this.container?.setVisible(this.shouldShow());
    if (this.fullscreenBtn) {
      const avail = (this.scene.scale as unknown as { fullscreen: { available: boolean } }).fullscreen.available;
      this.fullscreenBtn.setVisible(avail && this.shouldShow());
    }
    if (this.domRoot) this.domRoot.style.display = this.shouldShow() ? 'block' : 'none';
  }

  private addFullscreenButton(): void {
    const cw = this.scene.cameras.main.width;
    const radius = 18;
    const bg = this.scene.add.circle(0, 0, radius, 0x000000, 0.35);
    bg.setStrokeStyle(1.5, 0xffffff, 0.55);
    const icon = this.scene.add.text(0, 0, '⛶', { fontSize: '16px', color: '#fff', fontFamily: 'monospace', fontStyle: 'bold' }).setOrigin(0.5);
    this.fullscreenIcon = icon;
    const cont = this.scene.add.container(cw - 28, 28, [bg, icon]);
    cont.setSize(radius * 2 + 12, radius * 2 + 12).setScrollFactor(0).setDepth(1000);
    bg.setInteractive(new Phaser.Geom.Circle(0, 0, radius + 10), Phaser.Geom.Circle.Contains);
    cont.setInteractive(new Phaser.Geom.Circle(0, 0, radius + 10), Phaser.Geom.Circle.Contains);
    const toggle = () => {
      try { if (this.scene.scale.isFullscreen) this.scene.scale.stopFullscreen(); else this.scene.scale.startFullscreen(); } catch {
        try { if (document.fullscreenElement) document.exitFullscreen(); else document.documentElement.requestFullscreen?.(); } catch { /* ignore */ }
      }
    };
    const onDown = () => { bg.setFillStyle(0x000000, 0.65); toggle(); };
    bg.on('pointerdown', onDown); cont.on('pointerdown', onDown);
    const onUp = () => bg.setFillStyle(0x000000, 0.35);
    bg.on('pointerup', onUp); cont.on('pointerup', onUp); bg.on('pointerout', onUp); cont.on('pointerout', onUp);
    this.fullscreenBtn = cont; this.container!.add(cont); cont.setVisible(this.shouldShow()); this.updateFullscreenIcon();
  }

  private updateFullscreenIcon(): void {
    const isFs = this.scene.scale.isFullscreen || !!document.fullscreenElement;
    const txt = isFs ? '✕' : '⛶';
    if (this.fullscreenIcon) this.fullscreenIcon.setText(txt);
    const domFs = this.domButtons.get('fs');
    if (domFs && domFs.firstChild) (domFs.firstChild as HTMLElement).textContent = txt;
  }

  refreshVisibility(): void {
    const show = this.shouldShow();
    this.container?.setVisible(show);
    if (this.fullscreenBtn) {
      const avail = (this.scene.scale as unknown as { fullscreen: { available: boolean } }).fullscreen.available;
      this.fullscreenBtn.setVisible(avail && show);
    }
    if (this.domRoot) this.domRoot.style.display = show ? 'block' : 'none';
  }

  private reparentDomForFullscreen(entering: boolean): void {
    if (!this.domRoot) return;
    try {
      if (entering) {
        const fsTarget = (this.scene.scale as unknown as { fullscreenTarget: HTMLElement | null }).fullscreenTarget
          || document.fullscreenElement as HTMLElement | null
          || document.documentElement;
        if (fsTarget && this.domRoot.parentElement !== fsTarget) {
          fsTarget.appendChild(this.domRoot);
          // Asegurar que cubra el viewport en fullscreen
          this.domRoot.style.position = 'absolute';
          this.domRoot.style.inset = '0';
          this.domRoot.style.zIndex = '9999';
        }
      } else {
        const orig = this.domOriginalParent || document.getElementById('game-container');
        if (orig && this.domRoot.parentElement !== orig) {
          orig.appendChild(this.domRoot);
          this.domRoot.style.zIndex = '10';
        }
      }
    } catch { /* ignore */ }
    this.updateFullscreenIcon();
  }

  private addButton(x: number, y: number, radius: number, icon: string, onDown: () => void, onUp: () => void, label?: string): void {
    const bg = this.scene.add.circle(0, 0, radius, 0x000000, 0.35);
    bg.setStrokeStyle(2, 0xffffff, 0.55);
    const txt = this.scene.add.text(0, label ? -4 : 0, icon, { fontSize: `${Math.round(radius * 0.75)}px`, color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold' }).setOrigin(0.5);
    const cont = this.scene.add.container(x, y, [bg, txt]);
    if (label) {
      const lb = this.scene.add.text(0, radius - 10, label, { fontSize: '8px', color: '#ffffff', fontFamily: 'monospace' }).setOrigin(0.5);
      cont.add(lb);
    }
    bg.setInteractive(new Phaser.Geom.Circle(0, 0, radius + 12), Phaser.Geom.Circle.Contains);
    cont.setSize(radius * 2 + 24, radius * 2 + 24);
    const setPressed = (p: boolean) => { bg.setFillStyle(0x000000, p ? 0.65 : 0.35); bg.setScale(p ? 0.94 : 1); txt.setAlpha(p ? 1 : 0.9); };
    bg.on('pointerdown', () => { setPressed(true); onDown(); });
    const release = () => { setPressed(false); onUp(); };
    bg.on('pointerup', release); bg.on('pointerupoutside', release); bg.on('pointercancel', release);
    bg.on('pointerout', () => { const isDown = (bg.input as unknown as { isDown?: boolean })?.isDown; if (isDown) release(); });
    cont.setInteractive(new Phaser.Geom.Circle(0, 0, radius + 12), Phaser.Geom.Circle.Contains);
    cont.on('pointerdown', () => { setPressed(true); onDown(); });
    cont.on('pointerup', release); cont.on('pointerupoutside', release); cont.on('pointercancel', release);
    this.buttons.push(cont); this.container!.add(cont);
  }

  getMoveDirection(): number { return (this.rightHeld ? 1 : 0) - (this.leftHeld ? 1 : 0); }
  get isSprintHeld(): boolean { return this.sprintHeld; }
  get isJumpHeld(): boolean { return this.jumpHeld; }
  consumeJumpPressed(): boolean { if (this.jumpPressPending) { this.jumpPressPending = false; return true; } return false; }
  consumeJumpReleased(): boolean { if (this.jumpReleasePending) { this.jumpReleasePending = false; return true; } return false; }
  consumeStonePressed(): boolean { if (this.stonePressPending) { this.stonePressPending = false; return true; } return false; }

  destroy(): void {
    if (this.resizeHandler) { this.scene.scale.off('resize', this.resizeHandler); this.resizeHandler = null; }
    this.scene.scale.off('enterfullscreen', this.updateFullscreenIcon);
    this.scene.scale.off('leavefullscreen', this.updateFullscreenIcon);
    this.scene.scale.off('fullscreenfailed', this.updateFullscreenIcon);
    document.removeEventListener('fullscreenchange', this.boundReparent as EventListener);
    this.buttons = []; this.fullscreenBtn = null; this.fullscreenIcon = null;
    this.container?.destroy(); this.container = null;
    if (this.domRoot) { this.domRoot.remove(); this.domRoot = null; this.domButtons.clear(); }
  }
}
